import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, withNext } from "@/lib/api";
import { PlanToggle } from "@/components/PlanToggle";
import { FreeCard, ProCard } from "@/components/PricingCards";
import { PricingFaq } from "@/components/PricingFaq";
import { Loader } from "@/components/Loader";
import { PublicHeader } from "@/components/PublicHeader";
import { PlanCompare } from "@/components/PlanCompare";

export default function PricingPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [status, setStatus] = useState(null);
  const [interval, setPeriod] = useState("month");
  useEffect(() => { api.get("/billing/plans").then((r) => setPlans(r.data)); }, []);
  useEffect(() => { if (user) api.get("/billing/status").then((r) => setStatus(r.data)).catch(() => {}); }, [user]);
  if (!plans || user === null) return <Loader />;

  return (
    <div className="relative z-10 min-h-screen" data-testid="pricing-page">
      <PublicHeader testId="pricing-brand-link" />
      <main className="px-6 md:px-14 pt-10 pb-24 max-w-5xl mx-auto">
        <p className="font-code text-xs uppercase tracking-[0.3em] text-amber-500/80 mb-3 rise">Pricing</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-amber-50 max-w-3xl rise">Every wiki and writing tool is free. Pro gives you room, teamwork and your own look.</h1>
        <p className="text-slate-400 mt-5 max-w-xl rise">Start with two worlds, three works and the whole toolkit. Go Pro when your stories need more room or more hands.</p>
        <div className="mt-12 mb-6"><PlanToggle value={interval} onChange={setPeriod} /></div>
        <div className="grid md:grid-cols-2 gap-6 items-start">
          <FreeCard user={user} limit={plans.worlds_limit} />
          <ProCard user={user} status={status} interval={interval} prices={plans.prices} />
        </div>
        <Link to={user ? "/gift" : withNext("/register", "/gift")} className="mt-6 flex items-center gap-3 rounded-2xl border border-dashed border-amber-500/30 px-6 py-4 text-sm text-slate-300 hover:border-amber-500/60 transition-colors" data-testid="pricing-gift-link">
          <Gift className="w-5 h-5 text-amber-400 shrink-0" />
          <span><span className="text-amber-100">Give Pro as a gift.</span> A year of unlimited worlds for ${plans.gift_price}, with your own note.</span>
        </Link>
        <section className="mt-20" data-testid="pricing-compare-section">
          <h2 className="font-display text-3xl text-amber-50 mb-2">Free and Pro, side by side</h2>
          <p className="text-sm text-slate-400 mb-8">Both plans get the full wiki and writing desk. Pro adds room, teamwork and the look you want.</p>
          <PlanCompare />
          <Link to="/features" className="mt-5 inline-flex text-sm text-amber-300 hover:text-amber-200" data-testid="pricing-features-link">See every feature in detail →</Link>
        </section>
        <section className="mt-24">
          <h2 className="font-display text-3xl text-amber-50 mb-8">Questions, answered</h2>
          <PricingFaq />
        </section>
      </main>
    </div>
  );
}
